import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Barcode, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function FillEan({ onFilled }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [done, setDone] = useState(null);

  const openPreview = async () => {
    setOpen(true); setPlan(null); setDone(null); setLoading(true);
    try {
      const { data } = await api.get("/producten/fill-ean/preview");
      setPlan(data);
    } catch {
      toast.error("Kon preview niet laden");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    setExecuting(true);
    try {
      const { data } = await api.post("/producten/fill-ean");
      setDone(data);
      toast.success(`${data.aangevuld} EAN('s) aangevuld`);
      onFilled?.();
    } catch {
      toast.error("Aanvullen mislukt");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={openPreview} data-testid="fill-ean-btn">
        <Barcode className="h-4 w-4 mr-1" /> Ontbrekende EAN's aanvullen
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPlan(null); setDone(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="fill-ean-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Barcode className="h-5 w-5 text-primary" /> Ontbrekende EAN's aanvullen
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Preview laden…
            </div>
          )}

          {done && (
            <div className="space-y-2 text-sm py-6 text-center" data-testid="fill-ean-done">
              <Check className="h-8 w-8 text-primary mx-auto" />
              <div className="font-medium text-base">Klaar</div>
              <div className="text-muted-foreground">{done.aangevuld} EAN('s) aangevuld.</div>
            </div>
          )}

          {!loading && !done && plan && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                EAN's worden uit de verkoopfactuurregels gehaald en gekoppeld op productnaam.
                Hieronder zie je welke producten een EAN krijgen.
              </p>
              <div className="flex gap-2">
                <Badge variant="outline">{plan.aanvullen.length} aan te vullen</Badge>
                <Badge variant="outline" className="text-amber-600 border-amber-300">{plan.zonder_ean_totaal} zonder EAN totaal</Badge>
              </div>

              <div className="border border-border rounded-lg overflow-y-auto max-h-[46vh] divide-y divide-border" data-testid="fill-ean-list">
                {plan.aanvullen.map((a) => (
                  <div key={a.id} className="p-3 flex items-center gap-2" data-testid={`fill-ean-row-${a.sku}`}>
                    <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">{a.sku}</span>
                    <span className="font-medium truncate flex-1">{a.naam}</span>
                    <span className="font-mono text-xs text-primary">{a.ean}</span>
                  </div>
                ))}
                {plan.aanvullen.length === 0 && (
                  <div className="p-6 text-center text-muted-foreground">
                    Geen ontbrekende EAN's gevonden om aan te vullen.
                    {plan.zonder_ean_totaal > 0 && ` De ${plan.zonder_ean_totaal} resterende producten zonder EAN (diensten, verzendkosten, displays) komen niet als fysiek artikel in verkoopregels voor.`}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {done ? (
              <Button onClick={() => setOpen(false)} data-testid="fill-ean-close">Sluiten</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={execute} disabled={executing || loading || !plan || plan.aanvullen.length === 0}
                  data-testid="fill-ean-confirm">
                  {executing ? "Bezig…" : `Aanvullen (${plan?.aanvullen.length || 0})`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
